export interface TreeNode {
  id: string;
  label: string;
  children?: TreeNode[];
  hint?: string;
  disabled?: boolean;
}

export type CheckState = 'checked' | 'unchecked' | 'indeterminate';

export function collectLeaves(nodes: TreeNode[]): string[] {
  const out: string[] = [];
  const walk = (list: TreeNode[]): void => {
    for (const node of list) {
      if (node.disabled) continue;
      if (!node.children?.length) out.push(node.id);
      else walk(node.children);
    }
  };
  walk(nodes);
  return out;
}

export function leavesUnder(nodes: TreeNode[], id: string): string[] {
  const found = findNode(nodes, id);
  if (!found) return [];
  if (!found.children?.length) return [found.id];
  return collectLeaves(found.children);
}

export function findNode(nodes: TreeNode[], id: string): TreeNode | undefined {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children) {
      const hit = findNode(node.children, id);
      if (hit) return hit;
    }
  }
  return undefined;
}

export function nodeState(node: TreeNode, selected: ReadonlySet<string>): CheckState {
  if (!node.children?.length) return selected.has(node.id) ? 'checked' : 'unchecked';
  const leaves = collectLeaves(node.children);
  const n = leaves.filter((id) => selected.has(id)).length;
  if (n === 0) return 'unchecked';
  if (n === leaves.length) return 'checked';
  return 'indeterminate';
}

export function toggleNode(nodes: TreeNode[], selected: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(selected);
  const leaves = leavesUnder(nodes, id);
  const allOn = leaves.every((leaf) => next.has(leaf));
  for (const leaf of leaves) {
    if (allOn) next.delete(leaf);
    else next.add(leaf);
  }
  return next;
}

export function filterTree(nodes: TreeNode[], query: string): TreeNode[] {
  const q = query.trim().toLowerCase();
  if (!q) return nodes;
  const keep = (node: TreeNode): TreeNode | null => {
    const self = node.id.toLowerCase().includes(q) || node.label.toLowerCase().includes(q);
    const children = (node.children ?? []).map(keep).filter((c): c is TreeNode => c !== null);
    if (self || children.length) return { ...node, children: node.children ? children : undefined };
    return null;
  };
  return nodes.map(keep).filter((n): n is TreeNode => n !== null);
}

export function expandMatching(nodes: TreeNode[], query: string): Set<string> {
  const open = new Set<string>();
  const q = query.trim().toLowerCase();
  const walk = (list: TreeNode[], ancestors: string[]): void => {
    for (const node of list) {
      const match = q !== '' && (node.id.toLowerCase().includes(q) || node.label.toLowerCase().includes(q));
      if (match) for (const a of ancestors) open.add(a);
      if (node.children) walk(node.children, [...ancestors, node.id]);
    }
  };
  walk(nodes, []);
  return open;
}

export function buildColonTree(values: string[]): TreeNode[] {
  const root = new Map<string, TreeNode>();
  for (const value of [...values].sort()) {
    const parts = value.split(':').filter(Boolean);
    if (parts.length === 0) continue;
    const domain = parts[0]!;
    if (!root.has(domain)) root.set(domain, { id: domain, label: domain, children: [] });
    const domainNode = root.get(domain)!;
    if (parts.length === 1) continue;
    if (parts.length === 2) {
      domainNode.children!.push({ id: value, label: parts[1]! });
      continue;
    }
    const actionId = `${parts[0]}:${parts[1]}`;
    let action = domainNode.children!.find((c) => c.id === actionId);
    if (!action) {
      action = { id: actionId, label: parts[1]!, children: [] };
      domainNode.children!.push(action);
    }
    action.children ??= [];
    action.children.push({ id: value, label: parts.slice(2).join(':') });
  }
  return [...root.values()];
}

export function buildDottedTree(items: Array<{ type: string; version?: string; label?: string }>): TreeNode[] {
  type Mutable = { id: string; label: string; children: Map<string, Mutable> };
  const root = new Map<string, Mutable>();
  for (const item of items) {
    const parts = item.type.split('.').filter(Boolean);
    let cursor = root;
    let path = '';
    parts.forEach((part, index) => {
      path = path ? `${path}.${part}` : part;
      const id = index === parts.length - 1 && item.version ? `${item.type}@${item.version}` : path;
      if (!cursor.has(path)) cursor.set(path, { id, label: part, children: new Map() });
      const node = cursor.get(path)!;
      if (index === parts.length - 1) node.id = id;
      cursor = node.children;
    });
  }
  const toTree = (map: Map<string, Mutable>): TreeNode[] =>
    [...map.values()].map((n) => ({
      id: n.id,
      label: n.label,
      children: n.children.size ? toTree(n.children) : undefined,
    }));
  return toTree(root);
}

export function buildCategoryTree(
  items: Array<{ id: string; category: string; label: string; extra?: string }>,
): TreeNode[] {
  const groups = new Map<string, TreeNode>();
  for (const item of items) {
    const cat = item.category || 'autre';
    if (!groups.has(cat)) groups.set(cat, { id: `cat:${cat}`, label: cat, children: [] });
    groups.get(cat)!.children!.push({
      id: item.id,
      label: item.extra ? `${item.label} (${item.extra})` : item.label,
    });
  }
  return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label));
}
